import React, { useCallback, useEffect, useState } from "react";

import { Grid, Tooltip } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ShipeeziGrid from "Components/AppGrid/shipeezi-new-grid";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import { FiLink2 } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";

import { ExternalList } from "../../../context/MovingResources";
import { useAppState } from "../../../context/StateContext";
import { Destination } from "../../../models/destination";
import { JobMode } from "../../../models/job.mode";
import { DifferentLocation as LocationAddress } from "../../../models/location";
import { Material } from "../../../models/material";
import { RoomJob } from "../../../models/sales";
import { ServiceBooking } from "../../../models/service.booking";
import { Searches } from "../../../models/shipeezi.table";
import DestinationService from "../../../services/destination.service";
import ModeJobService from "../../../services/mode.job.service";
import { Status } from "../../../util/enums";
import getAllMaterialsFromRoom from "../../../util/getAllMaterialsFromRoom";
import { getDestinations } from "../../../util/getDestinations";
import { getCurrency } from "../../../util/getMeasurement";
import { formatCurrency, getPriceStringValid } from "../../../util/prices";
import { Button, Input } from "../../Form";
import { Color } from "../../Form/Button";
import Checkbox from "../../Form/Checkbox";
import InputRange from "../../Form/InputRange";
import LoadingModal from "../../LoadingModal";
import ActionButtons from "../ActionButtons";
import { StyledMenu } from "../AnchorMenu";
import ModalAddItem from "./ModalAddItem";
import useStyles, { ContainerMaterialIcon, Subtitle, Title } from "./styles";
import SummaryList from "./SummaryList";
import { MOVING_AGENT_COMPANY_PROFILE_ID } from "Constants";

type Props = {
  inventory?: RoomJob[];
  dataCyCancel?: string;
  dataCySave?: string;
  jobId?: number;
  isUpdating?: boolean;
  destinations?: LocationAddress[];
  disableFields?: boolean;
  externalList?: ExternalList;
  isOnViewMode?: boolean;
  onCustomerView?: boolean;
  isSavingAndSending?: boolean;
  onSave?: () => void;
  onCancel?: () => void;
  handleUpdate?: (value: RoomJob[]) => void;
  onSavePublish?: () => void;
  onSaveAndSend?: () => void;
};

const PAGE_SIZE = 20;

const PackingMaterials = (props: Props) => {
  const {
    inventory,
    dataCyCancel,
    dataCySave,
    jobId,
    isUpdating,
    destinations,
    externalList,
    isOnViewMode = false,
    onCustomerView = false,
    isSavingAndSending = false,
    onSave,
    onCancel,
    handleUpdate,
    onSavePublish,
    onSaveAndSend,
  } = props;

  const { currentUser, agentPermissions } = useAppState();

  const [page, setPage] = useState(0);

  const [search, setSearch] = useState<Searches>();

  const [materials, setMaterials] = useState<Material[]>([]);
  const [customDestinations, setCustomDestinations] = useState<Destination[]>(
    externalList?.destinationList || []
  );
  const [modes, setModes] = useState<JobMode[]>(externalList?.jobModeList || []);

  const [modalAddItemIsOpen, setModalAddItemIsOpen] = useState<boolean>(false);

  const { t: translate } = useTranslation();

  const classes = useStyles();

  const currency = getCurrency();

  const [showRooms, setShowRooms] = useState(true);
  const [showDestinations, setShowDestinations] = useState(true);

  useQuery(
    ["packaging.destination.list"],
    () =>
      DestinationService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.destinationList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data) setCustomDestinations(response.data);
      },
    }
  );

  useQuery(
    ["packaging.mode.list"],
    () =>
      ModeJobService.getAll({
        companyId: currentUser?.companyId,
        status: Status.ACTIVE,
        sortBy: JSON.stringify([["name"]]),
      }),
    {
      enabled: !!currentUser?.companyId && !externalList?.jobModeList,
      refetchOnWindowFocus: false,
      onSuccess: (response) => {
        if (response && response.data && response.data instanceof Array) setModes(response.data);
      },
    }
  );

  useEffect(() => {
    if (inventory && inventory?.length > 0) getAllMaterials();
  }, [inventory]);

  const getAllMaterials = () => {
    let materials: Material[] = [];

    if (inventory && inventory?.length > 0)
      inventory?.forEach((roomJob) => {
        const allMaterials = getAllMaterialsFromRoom(roomJob);

        if (roomJob.materials) {
          materials = [...materials, ...allMaterials];
        }
      });

    setMaterials(materials);
  };

  const getTotalPriceOfMaterials = useCallback(() => {
    let totalPrice = 0;

    if (isOnViewMode && materials.length > 0) {
      materials.forEach((material) => {
        totalPrice += Number(Number(material.price ?? 0) * Number(material.quantity));
      });
    }

    return formatCurrency(totalPrice, currency);
  }, [materials, isOnViewMode]);

  const handleCurrentPage = (numPage: number) => {
    document.location.href = "#top";
    setPage(numPage - 1);
  };

  const onSearching = (search: Searches) => {
    setSearch(search);
  };

  const onDelete = (ids: string[]) => {
    if (inventory && inventory?.length > 0) {
      const inventoryUpdated = [...inventory];

      inventoryUpdated?.forEach((roomJob) => {
        ids.forEach((id) => {
          const index = roomJob?.materials?.findIndex(
            (material) => materialIdentifier(material) === id
          );

          if (index !== -1) {
            const { materials } = roomJob;
            const materialsUpdated = [...materials];
            const materialsDeletedUpdated = [
              ...(roomJob?.materialsDeleted || []),
              roomJob?.materials[index],
            ];

            delete materialsUpdated[index];

            roomJob.materials = materialsUpdated.filter(Boolean);
            roomJob.materialsDeleted = materialsDeletedUpdated;
          }
        });
      });
      if (handleUpdate) handleUpdate(inventoryUpdated);
    }
  };

  const materialsHasSame = (material1: Material, material2: Material) => {
    if (material2.id) return material1.id === material2.id;

    return material1.uuid === material2.uuid;
  };

  const materialIdentifier = (material: Material) =>
    `${material.id ? material.id : ""}-${material.uuid ? material.uuid : ""}`;

  const onChangeMaterial = (material: Material) => {
    if (inventory && inventory?.length > 0) {
      const inventoryUpdated = [...inventory];

      inventoryUpdated?.forEach((roomJob) => {
        const index = roomJob?.materials?.findIndex((materialItem) =>
          materialsHasSame(materialItem, material)
        );

        if (index !== -1) {
          const { materials } = roomJob;
          const materialsUpdated = [...materials];

          materialsUpdated[index] = material;
          roomJob.materials = materialsUpdated;
        }
      });
      if (handleUpdate) handleUpdate(inventoryUpdated);
    }
  };

  const onSaveAddItem = (material: Material) => {
    if (inventory && inventory?.length > 0) {
      let itemSaved = false;
      const inventoryUpdated = _.cloneDeep(inventory);

      inventoryUpdated?.forEach((roomJob) => {
        material.packingMaterialId = material.serviceBooking.id;

        if (roomJob?.id === material?.room?.id) {
          if (roomJob?.materials) {
            const { materials } = roomJob;
            const materialsUpdated = [...materials, material];

            roomJob.materials = materialsUpdated;
            itemSaved = true;
          }
        }
      });

      if (itemSaved) {
        if (handleUpdate) handleUpdate(inventoryUpdated);
        setModalAddItemIsOpen(false);
      }
    }
  };

  const getData = useCallback(() => {
    const data: Material[] = [];

    if (search && search.length > 0) {
      search.forEach((searchItem) => {
        const attribute = Object.keys(searchItem)[0];
        const value = Object.values(searchItem)[0];
        const attributes: string[] = [];

        if (attribute.includes(".")) {
          attribute.split(".").forEach((attributeItem) => attributes.push(attributeItem));
        } else attributes.push(attribute);

        let response: Material[] = [];

        if (attributes.length > 1) {
          response = materials?.filter((material) => {
            const child = material[attributes[0] as keyof Material];

            return String(child?.[attributes[1] as keyof typeof child])?.includes(value);
          });
        } else if (attributes.length === 1) {
          response = materials?.filter((material) => {
            if (attributes[0] === "destination") {
              if (material.destination)
                return String(material.destination.name).includes(String(value));
              if (material.destinationResidence)
                return String(material.destinationResidence.longAddress).includes(String(value));
            }

            return String(material[attributes[0] as keyof Material]).includes(String(value));
          });
        }

        data.push(...response);
      });

      return data;
    }

    return materials;
  }, [search, materials]);

  const paginate = (materials: Material[], pageSize: number, pageNumber: number) => {
    return materials.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  };

  const renderInputQuantity = (material: Material) => {
    return isOnViewMode || material?.itemJobId ? (
      <span>{material?.quantity ?? 0}</span>
    ) : (
      <div className={classes.inputQuantity}>
        <InputRange
          disabled={
            !!material?.itemJobId ||
            !(
              currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
              agentPermissions?.[Number(jobId)]?.order?.sections?.material?.fields?.general?.edit === true
            )
          }
          name="quantity"
          value={material?.quantity ?? 0}
          change={(value: number) =>
            onChangeMaterial({
              ...material,
              quantity: value,
            } as Material)
          }
          range={1}
          onlyIntValues
        />
      </div>
    );
  };

  const renderShowButton = () => {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const open = !!anchorEl;

    return (
      <div>
        <Button
          customColor={Color.TEXT_INFO}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          className={classes.actionButtonShow}
          endIcon={<KeyboardArrowDownIcon />}
        >
          {translate("show")}
        </Button>
        <StyledMenu
          id="actionShow"
          anchorEl={anchorEl}
          keepMounted
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          PaperProps={{
            style: {
              marginTop: "7px",
            },
          }}
        >
          <div className={classes.actionButtonShowBody}>
            <Checkbox
              label={translate("rooms")}
              disabled
              checked={showRooms}
              onChange={(_, checked) => setShowRooms(checked)}
            />
            <Checkbox
              label={translate("destinations")}
              checked={showDestinations}
              onChange={(_, checked) => setShowDestinations(checked)}
            />
          </div>
        </StyledMenu>
      </div>
    );
  };

  const onChangeMaterialPrice = (price?: number, key?: string | number) => {
    if (!key) return;
    const property = typeof key === "string" ? "uuid" : ("id" as keyof Material);
    const newInventory: RoomJob[] = JSON.parse(JSON.stringify(inventory || []));
    let hasChanged = false;

    newInventory?.forEach((invent) => {
      invent?.materials?.forEach((material) => {
        if (material[property] === key) {
          if (Number(material.price) !== Number(price)) {
            material.price = price && !Number.isNaN(price) ? price : 0;
            hasChanged = true;
          }
        }
      });
    });
    if (handleUpdate && hasChanged) {
      handleUpdate(newInventory);
    }
  };

  const [localPrice, setLocalPrice] = useState("");
  const [materialFocused, setMaterialFocused] = useState<string | number>();
  const renderInputPrice = useCallback(
    (material: Material) => {
      return isOnViewMode || material?.itemJobId ? (
        <span>{formatCurrency(material.price ?? 0, currency)}</span>
      ) : (
        <div className={classes.inputPrice}>
          <Input
            disabled={!!material?.itemJobId}
            value={
              materialFocused && [material.id, material.uuid].includes(materialFocused)
                ? localPrice
                : material.price?.toString()
            }
            type="text"
            onFocus={() => {
              setLocalPrice(String(material.price));
              setMaterialFocused(material.id || material.uuid);
            }}
            onChange={(event) => {
              const decimalSeparator = currency?.decimalSeparator ?? ".";
              const decimalPoints = currency?.decimalPoints ?? 2;

              const withPointer = getPriceStringValid(event.target.value, decimalSeparator);

              if (withPointer.includes(decimalSeparator)) {
                const verifyPrice = withPointer.split(decimalSeparator);

                if (verifyPrice[1].length <= Number(decimalPoints)) {
                  setLocalPrice(withPointer);
                }
              } else setLocalPrice(withPointer);
            }}
            onBlur={() => {
              onChangeMaterialPrice(
                currency?.decimalSeparator === ","
                  ? Number(localPrice.replace(",", ".")) || 0
                  : Number(localPrice) || 0,
                material.id || material.uuid
              );
              setLocalPrice("");
              setMaterialFocused(undefined);
            }}
            placeholder="0.00"
            leftIcon={"$"}
          />
        </div>
      );
    },
    [materialFocused, localPrice]
  );

  const formatDestinationText = (material: Material) => {
    let destination: string = "";

    if (material?.destination) destination = material.destination.name;
    if (material?.destinationResidence)
      destination = `Residence: ${material.destinationResidence.longAddress}`;
    if (material?.modeDestination) destination = material.modeDestination.name;

    return <span>{destination}</span>;
  };

  const dataGrid = getData();
  const totalPrice = dataGrid.reduce((prev, curr) => prev + Number(curr.price || 0), 0);

  return (
    <div>
      <div className={isOnViewMode ? classes.containerView : classes.container}>
        <ModalAddItem
          inventory={inventory || ([] as RoomJob[])}
          modalAddItemIsOpen={modalAddItemIsOpen}
          jobId={jobId}
          onClose={() => setModalAddItemIsOpen(false)}
          onSave={onSaveAddItem}
          destinations={getDestinations(customDestinations, destinations || [], modes)}
          locations={destinations || []}
          materialList={externalList?.materialList}
        />
        <Grid container direction="row" alignItems="flex-start">
          {!onCustomerView ? (
            <Grid item xs={12} md={12} lg={12}>
              <div
                data-cy="grid-packing-materials"
                className={isOnViewMode ? classes.scrollHiddenView : classes.scrollHidden}
              >
                {!isOnViewMode ? (
                  <div className={classes.headerTitleContainer}>
                    <Title>{translate("material")}</Title>
                    <Subtitle>
                      {`\u00A0 \u00A0 •  \u00A0 ${translate("total")} ${translate(
                        "price"
                      ).toLowerCase()}: $${totalPrice.toFixed(2)}`}
                    </Subtitle>
                  </div>
                ) : null}
                <ShipeeziGrid
                  columns={[
                    {
                      name: "serviceBooking",
                      searchIdentifier: "serviceBooking.code",
                      title: `${translate("sku")} #`,
                      formatText: (serviceBooking: ServiceBooking) => serviceBooking?.code ?? "",
                    },
                    {
                      name: "room",
                      title: translate("room"),
                      searchIdentifier: "room.name",
                      formatComponent: (room: RoomJob, material: Material) => {
                        if (inventory && inventory?.length > 0) {
                          if (room) {
                            const roomName = inventory?.find(
                              (roomJob) => roomJob?.name === room?.name
                            )?.name;

                            if (roomName) return roomName;
                          }

                          const roomId = material?.roomId || material?.itemJob?.roomId || undefined;

                          if (roomId) {
                            const roomName = inventory?.find(
                              (roomJob) => roomJob?.id === roomId
                            )?.name;

                            if (roomName) return roomName;
                          }
                        }

                        return "";
                      },
                    },
                    {
                      name: "destination",
                      title: translate("destination"),
                      formatComponent: (_: number, data: Material) => formatDestinationText(data),
                    },
                    {
                      name: "serviceBooking",
                      title: translate("productName"),
                      searchIdentifier: "serviceBooking.name",
                      formatComponent: (_, material) => {
                        return (
                          <ContainerMaterialIcon>
                            {material?.serviceBooking?.name || ""}
                            {material?.itemJobId && (
                              <Tooltip
                                placement="top"
                                title={`${translate("linkedTo")} ${material?.itemJob?.description}`}
                              >
                                <div className={classes.containerIconLink}>
                                  <FiLink2 className={classes.ml12} color="#0055A4" />
                                </div>
                              </Tooltip>
                            )}
                          </ContainerMaterialIcon>
                        );
                      },
                    },
                    {
                      name: "quantity",
                      title: translate("quantity"),
                      formatComponent: (_: number, data: Material) => renderInputQuantity(data),
                    },
                    {
                      name: "price",
                      title: translate("price"),
                      formatComponent: (_: number, data: Material) => renderInputPrice(data),
                      className: isOnViewMode ? classes.customColumnView : undefined,
                    },
                  ]}
                  data={paginate(dataGrid, PAGE_SIZE, page + 1)}
                  keyExtractor={(data: Material) => materialIdentifier(data)}
                  totalItems={dataGrid.length}
                  pageSize={PAGE_SIZE}
                  emptyData={translate("noInformationToBeShown")}
                  namePage={translate("material")}
                  showSort={false}
                  showFooterPageInfo={false}
                  onNextPage={handleCurrentPage}
                  onPreviousPage={handleCurrentPage}
                  onGoToPage={handleCurrentPage}
                  showSelection={!isOnViewMode}
                  showFooterGrid={!isOnViewMode}
                  rowActionsExtra={
                    !isOnViewMode &&
                    (currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
                      agentPermissions?.[Number(jobId)]?.order?.sections?.material?.fields?.general?.edit === true)
                      ? [
                          {
                            title: "Delete",
                            buttonAndAction: (id: number, material: Material) =>
                              material?.itemJobId ? undefined : (
                                <Tooltip title={"Delete"} placement="top">
                                  <div>
                                    <RiDeleteBinLine
                                      className={classes.button}
                                      size={18}
                                      onClick={() => onDelete([String(id)])}
                                      color="#F97066"
                                    />
                                  </div>
                                </Tooltip>
                              ),
                          },
                        ]
                      : undefined
                  }
                  showHeaderGrid={!isOnViewMode}
                  generalActions={
                    !isOnViewMode
                      ? {
                          orderBy: {
                            left: ["handleSearch", "handleView"],
                            right: ["handleNewRecord"],
                          },
                          ...(currentUser?.company.profileId !== MOVING_AGENT_COMPANY_PROFILE_ID ||
                          agentPermissions?.[Number(jobId)]?.order?.sections?.material?.fields?.general?.addNew ===
                            true
                            ? {
                                handleNewRecord: {
                                  text: translate("addNewMaterial"),
                                  action: () => setModalAddItemIsOpen(true),
                                  tooltip: translate("addNewMaterial"),
                                },
                              }
                            : {}),
                          handleSearch: {
                            inputSearchMode: "enter",
                            onSearch: (filters) => onSearching(filters),
                          },
                          handleView: renderShowButton(),
                        }
                      : undefined
                  }
                  positionActionsColumn="right"
                />
                {isOnViewMode && (
                  <div className={classes.viewStyle}>
                    <section className={classes.totalWrapper}>
                      <span>{translate("total")}</span>
                      <p>{getTotalPriceOfMaterials()}</p>
                    </section>
                  </div>
                )}
              </div>
            </Grid>
          ) : (
            <Typography fontFamily="Manrope" fontSize="18px" fontWeight="700">
              {translate("material")}
            </Typography>
          )}
          {!isOnViewMode && !showDestinations ? null : (
            <Grid item xs={12} md={12} lg={12}>
              <div className={isOnViewMode && !onCustomerView ? classes.withBorder : ""}>
                <SummaryList
                  roomJobList={inventory || ([] as RoomJob[])}
                  onCustomerView={onCustomerView}
                />
              </div>
            </Grid>
          )}
        </Grid>
        {!isOnViewMode && (
          <div className={classes.buttons}>
            <ActionButtons
              cancelDisabled={isUpdating}
              saveDisabled={isUpdating}
              dataCyCancel={dataCyCancel}
              dataCySave={dataCySave}
              showSaveAndSendButton={isSavingAndSending}
              onCancel={onCancel}
              onSave={onSave}
              onSavePublish={!isUpdating ? onSavePublish : undefined}
              onSaveAndSend={onSaveAndSend}
            />
          </div>
        )}
      </div>
      <LoadingModal showing={isUpdating} />
    </div>
  );
};

export default PackingMaterials;
